import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { 
  History, Search, Filter, Calendar, User, ArrowUpRight, 
  ArrowDownLeft, CheckCircle2, DollarSign, ShoppingCart, 
  Package, TrendingUp, Layers, RefreshCw, Download, 
  ChevronRight, ChevronLeft, Shield, ShieldCheck, Clock, FileText, Factory,
  Sparkles, Eye, ArrowRight, Building2, CalendarDays, ExternalLink, Copy, Check
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter 
} from '@/components/ui/dialog';
import { fetchUnifiedActivityLogs, PORTAL_STRUCTURE, OFFICIAL_TEAM_MEMBERS } from '@/utils/activityLogger';
import { exportToPDF } from '@/utils/exportPDF';
import { base44 } from '@/api/base44Client';

const ALL_PORTALS = [
  { id: 'all', name: 'Semua Portal', icon: Layers, color: 'bg-slate-800 text-white', badgeColor: 'bg-slate-100 text-slate-800' },
  ...PORTAL_STRUCTURE.map(p => {
    let icon = Layers;
    if (p.id === 'finance') icon = DollarSign;
    if (p.id === 'purchasing') icon = ShoppingCart;
    if (p.id === 'production') icon = Factory;
    if (p.id === 'marketing') icon = TrendingUp;
    if (p.id === 'workspace') icon = ShieldCheck;
    if (p.id === 'analytics') icon = FileText;
    return { ...p, icon };
  })
];

export default function WorkspaceActivityLog() {
  const navigate = useNavigate();
  const [activePortal, setActivePortal] = useState('all');
  const [activeSubPage, setActiveSubPage] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStaff, setSelectedStaff] = useState('all');
  const [selectedActionType, setSelectedActionType] = useState('all');
  
  // Date Filtering Mode: 'daily' (default) | 'weekly' | 'monthly' | 'alltime' | 'custom'
  const [dateMode, setDateMode] = useState('daily');
  const [selectedDay, setSelectedDay] = useState(new Date().toISOString().split('T')[0]);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  // Inspection Modal State
  const [inspectLog, setInspectLog] = useState(null);
  const [isCopiedRef, setIsCopiedRef] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);

  const { data: logs = [], isLoading, isFetching, refetch } = useQuery({
    queryKey: ['unified-activity-logs'],
    queryFn: fetchUnifiedActivityLogs,
    staleTime: 10000,
    refetchInterval: 15000
  });

  // Query actual active team members dynamically from DB
  const { data: teamMembers = [] } = useQuery({
    queryKey: ['team-members'],
    queryFn: () => base44.entities.TeamMember.list('-join_date'),
    staleTime: 30000
  });

  const activeStaffList = useMemo(() => {
    if (!teamMembers || teamMembers.length === 0) {
      return OFFICIAL_TEAM_MEMBERS;
    }
    return teamMembers
      .filter(m => !m.deleted_at && m.status !== 'inactive')
      .map(m => ({
        name: m.full_name || m.name,
        role: m.position || m.role || 'Staff'
      }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [teamMembers]);

  // Current portal definition & available sub-pages
  const currentPortalObj = useMemo(() => {
    return PORTAL_STRUCTURE.find(p => p.id === activePortal) || null;
  }, [activePortal]);

  const availableSubPages = useMemo(() => {
    if (!currentPortalObj) {
      const allPages = [{ id: 'all', name: 'Semua Halaman Portal' }];
      PORTAL_STRUCTURE.forEach(p => {
        p.pages.forEach(pg => {
          if (pg.id !== 'all' && !allPages.some(x => x.id === pg.id)) {
            allPages.push({ ...pg, portalName: p.name });
          }
        });
      });
      return allPages;
    }
    return currentPortalObj.pages;
  }, [currentPortalObj]);

  // Helper for Date Shift
  const handleShiftDay = (direction) => {
    const d = new Date(selectedDay);
    d.setDate(d.getDate() + direction);
    setSelectedDay(d.toISOString().split('T')[0]);
    setCurrentPage(1);
  };

  // 1. Filter Logs by Date Period first
  const dateFilteredLogs = useMemo(() => {
    const now = new Date();

    return logs.filter(item => {
      const itemDateStr = item.timestamp ? new Date(item.timestamp).toISOString().split('T')[0] : '';
      const itemTime = item.timestamp ? new Date(item.timestamp).getTime() : 0;

      if (dateMode === 'daily') {
        if (itemDateStr !== selectedDay) return false;
      } else if (dateMode === 'weekly') {
        const diffDays = (now.getTime() - itemTime) / (1000 * 3600 * 24);
        if (diffDays < 0 || diffDays > 7) return false;
      } else if (dateMode === 'monthly') {
        const d = new Date(item.timestamp);
        if (d.getMonth() !== now.getMonth() || d.getFullYear() !== now.getFullYear()) return false;
      } else if (dateMode === 'custom') {
        if (customFrom && itemDateStr < customFrom) return false;
        if (customTo && itemDateStr > customTo) return false;
      }
      return true;
    });
  }, [logs, dateMode, selectedDay, customFrom, customTo]);

  // 2. Dynamic Counts per portal based on active date period
  const portalCounts = useMemo(() => {
    const counts = { all: dateFilteredLogs.length };
    ALL_PORTALS.forEach(p => {
      if (p.id !== 'all') {
        counts[p.id] = dateFilteredLogs.filter(l => l.portal === p.id).length;
      }
    });
    return counts;
  }, [dateFilteredLogs]);

  // 3. Dynamic Counts per sub-page in active portal based on active date period
  const subPageCounts = useMemo(() => {
    const counts = { all: activePortal === 'all' ? dateFilteredLogs.length : (portalCounts[activePortal] || 0) };
    availableSubPages.forEach(pg => {
      if (pg.id !== 'all') {
        counts[pg.id] = dateFilteredLogs.filter(l => {
          if (activePortal !== 'all' && l.portal !== activePortal) return false;
          return l.page === pg.id;
        }).length;
      }
    });
    return counts;
  }, [dateFilteredLogs, activePortal, availableSubPages, portalCounts]);

  // 4. Final Filtered Logs (Portal, SubPage, Staff, Action, Search)
  const filteredLogs = useMemo(() => {
    return dateFilteredLogs.filter(item => {
      // Portal Filter
      if (activePortal !== 'all' && item.portal !== activePortal) return false;

      // Sub-Page Filter
      if (activeSubPage !== 'all' && item.page !== activeSubPage) return false;

      // Staff Filter
      if (selectedStaff !== 'all') {
        const targetStaff = selectedStaff.toLowerCase();
        const userStaff = (item.user_name || '').toLowerCase();
        if (!userStaff.includes(targetStaff) && !targetStaff.includes(userStaff)) return false;
      }

      // Action Type Filter
      if (selectedActionType !== 'all') {
        if (selectedActionType === 'masuk' && item.type !== 'masuk' && item.action_type !== 'payment') return false;
        if (selectedActionType === 'keluar' && item.type !== 'keluar' && item.action_type !== 'expense') return false;
        if (selectedActionType === 'update' && item.action_type !== 'update') return false;
        if (selectedActionType === 'create' && item.action_type !== 'create') return false;
        if (selectedActionType === 'delete' && item.action_type !== 'delete') return false;
        if (selectedActionType === 'approval' && item.action_type !== 'approval') return false;
      }

      // Search Query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchTitle = item.title?.toLowerCase().includes(q);
        const matchDetails = item.details?.toLowerCase().includes(q);
        const matchUser = item.user_name?.toLowerCase().includes(q);
        const matchTarget = item.target_id?.toLowerCase().includes(q) || item.target_name?.toLowerCase().includes(q);
        const matchAcc = item.account?.toLowerCase().includes(q);
        if (!matchTitle && !matchDetails && !matchUser && !matchTarget && !matchAcc) return false;
      }

      return true;
    });
  }, [dateFilteredLogs, activePortal, activeSubPage, selectedStaff, selectedActionType, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage) || 1;
  const paginatedLogs = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredLogs.slice(start, start + itemsPerPage);
  }, [filteredLogs, currentPage, itemsPerPage]);

  const formatTimestamp = (ts) => {
    if (!ts) return '-';
    try {
      const d = new Date(ts);
      const day = String(d.getDate()).padStart(2, '0');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agt', 'Sep', 'Okt', 'Nov', 'Des'];
      const month = monthNames[d.getMonth()];
      const year = d.getFullYear();
      const hours = String(d.getHours()).padStart(2, '0');
      const mins = String(d.getMinutes()).padStart(2, '0');
      const secs = String(d.getSeconds()).padStart(2, '0');
      return `${day} ${month} ${year}, ${hours}:${mins}:${secs} WIB`;
    } catch {
      return String(ts);
    }
  };

  const formatDayTitle = (dateStr) => {
    try {
      const d = new Date(dateStr);
      const days = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
      const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
      const isToday = dateStr === new Date().toISOString().split('T')[0];
      return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()} ${isToday ? '(Hari Ini)' : ''}`;
    } catch {
      return dateStr;
    }
  };

  const getRelativeTime = (ts) => {
    if (!ts) return '';
    try {
      const diffMs = new Date().getTime() - new Date(ts).getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return 'Baru saja';
      if (diffMins < 60) return `${diffMins} menit lalu`;
      if (diffHours < 24) return `${diffHours} jam lalu`;
      if (diffDays === 1) return 'Kemarin';
      if (diffDays < 7) return `${diffDays} hari lalu`;
      return '';
    } catch {
      return '';
    }
  };

  const getPortalInfo = (portalId) => {
    return PORTAL_STRUCTURE.find(x => x.id === portalId) || { name: portalId, color: 'bg-slate-700 text-white', badgeColor: 'bg-slate-100 text-slate-700' };
  };

  const getSubPageInfo = (portalId, pageId) => {
    const p = PORTAL_STRUCTURE.find(x => x.id === portalId);
    if (!p) return { name: pageId, route: `/office/${portalId}` };
    const pg = p.pages.find(x => x.id === pageId);
    return pg ? { name: pg.name, route: pg.route || `/office/${portalId}` } : { name: pageId, route: `/office/${portalId}` };
  };

  const getDestinationUrl = (log) => {
    const subInfo = getSubPageInfo(log.portal, log.page);
    let route = subInfo.route || `/office/${log.portal}`;
    
    // Determine the isolated keyword
    let keyword = log.target_name || '';
    if (!keyword && log.target_id) keyword = log.target_id;
    if (!keyword && log.user_name && log.portal === 'workspace' && log.page === 'absensi') {
      keyword = log.user_name;
    }

    if (log.portal === 'production' && (log.page === 'po_list' || log.page === 'reports')) {
      return `/office/production/po-list?search=${encodeURIComponent(keyword)}`;
    }
    
    if (log.portal === 'finance' && (log.page === 'invoice_po' || log.page === 'invoices')) {
      return `/office/finance/invoices?search=${encodeURIComponent(keyword || log.target_id)}`;
    }

    if (log.portal === 'marketing' && (log.page === 'invoices' || log.page === 'sph_list')) {
      return `/office/marketing/invoices?search=${encodeURIComponent(keyword || log.target_id)}`;
    }

    if (log.portal === 'production' && log.page === 'sample') {
      return `/office/production/sample?search=${encodeURIComponent(keyword)}`;
    }

    if (log.portal === 'finance' && log.page === 'operasional') {
      return `/office/finance/operational?search=${encodeURIComponent(keyword)}`;
    }

    if (log.portal === 'purchasing' && log.page === 'purchasing_requests') {
      return `/office/purchasing/purchasing-requests?search=${encodeURIComponent(keyword)}`;
    }

    if (log.portal === 'workspace' && log.page === 'absensi') {
      return `/office/workspace/absensi?search=${encodeURIComponent(keyword)}`;
    }

    if (keyword) {
      return `${route}?search=${encodeURIComponent(keyword)}`;
    }
    return route;
  };

  const handleCopyRef = (refId) => {
    if (!refId) return;
    navigator.clipboard.writeText(refId);
    setIsCopiedRef(true);
    setTimeout(() => setIsCopiedRef(false), 2000);
  };

  const handleRefClick = (refId) => {
    if (!refId) return;
    setSearchQuery(refId);
    setCurrentPage(1);
  };

  const handleExport = () => {
    const portalName = ALL_PORTALS.find(p => p.id === activePortal)?.name;
    exportToPDF({
      title: `Audit Log Aktivitas - ${portalName}`,
      subtitle: `Periode: ${dateMode === 'daily' ? formatDayTitle(selectedDay) : dateMode} • Total ${filteredLogs.length} aktivitas`,
      headers: ['Waktu (WIB)', 'Staff / User', 'Portal & Halaman', 'Aktivitas', 'Nominal / Detail'],
      data: filteredLogs.slice(0, 100).map(l => {
        const subInfo = getSubPageInfo(l.portal, l.page);
        return [
          formatTimestamp(l.timestamp),
          `${l.user_name} (${l.user_role})`,
          `${l.portal.toUpperCase()} > ${subInfo.name}`,
          l.title,
          l.amount ? `Rp ${Number(l.amount).toLocaleString('id-ID')}` : (l.details || '-')
        ];
      })
    });
  };

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto space-y-6 text-[#302621]">
      
      {/* Header Panel */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-gradient-to-r from-[#FFFDF9] via-[#FDFBF7] to-[#F9F5EC] p-6 rounded-2xl border border-[#E6D5B8]/60 shadow-xs">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-tr from-[#8C6208] to-[#C9A227] text-white flex items-center justify-center shadow-md shadow-amber-600/20">
            <History className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-black text-[#302621] tracking-tight">
              Log Aktivitas Sistem (Multi-Portal Audit Trail)
            </h1>
            <p className="text-xs text-[#7A695B] mt-0.5">
              Pantau seluruh riwayat aktivitas, verifikasi, input data, dan perubahan per portal dan per halaman.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 w-full md:w-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            className="border-[#E6D5B8] bg-white hover:bg-[#F9F5EC] text-[#8C6208] text-xs font-bold gap-1.5 rounded-xl h-9"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh Log
          </Button>

          <Button
            size="sm"
            onClick={handleExport}
            className="bg-[#8C6208] hover:bg-[#724F06] text-white text-xs font-bold gap-1.5 rounded-xl h-9 shadow-sm"
          >
            <Download className="w-3.5 h-3.5" />
            Ekspor PDF
          </Button>
        </div>
      </div>

      {/* Date Navigation & Period Presets Bar (Daily by Default) */}
      <Card className="border border-[#E6D5B8]/60 bg-[#FFFDF9] shadow-xs">
        <CardContent className="p-4 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          
          {/* Period Mode Selectors */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-bold text-[#7A695B] mr-1 flex items-center gap-1">
              <CalendarDays className="w-3.5 h-3.5 text-[#8C6208]" />
              Periode:
            </span>
            
            <button
              onClick={() => { setDateMode('daily'); setCurrentPage(1); }}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                dateMode === 'daily' 
                  ? 'bg-[#8C6208] text-white shadow-xs' 
                  : 'bg-white text-[#5C4D42] hover:bg-[#F9F5EC] border border-[#E6D5B8]/60'
              }`}
            >
              Per Hari (Default)
            </button>

            <button
              onClick={() => { setDateMode('weekly'); setCurrentPage(1); }}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                dateMode === 'weekly' 
                  ? 'bg-[#8C6208] text-white shadow-xs' 
                  : 'bg-white text-[#5C4D42] hover:bg-[#F9F5EC] border border-[#E6D5B8]/60'
              }`}
            >
              Per Minggu
            </button>

            <button
              onClick={() => { setDateMode('monthly'); setCurrentPage(1); }}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                dateMode === 'monthly' 
                  ? 'bg-[#8C6208] text-white shadow-xs' 
                  : 'bg-white text-[#5C4D42] hover:bg-[#F9F5EC] border border-[#E6D5B8]/60'
              }`}
            >
              Per Bulan
            </button>

            <button
              onClick={() => { setDateMode('alltime'); setCurrentPage(1); }}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                dateMode === 'alltime' 
                  ? 'bg-[#8C6208] text-white shadow-xs' 
                  : 'bg-white text-[#5C4D42] hover:bg-[#F9F5EC] border border-[#E6D5B8]/60'
              }`}
            >
              Semua Waktu
            </button>

            <button
              onClick={() => { setDateMode('custom'); setCurrentPage(1); }}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                dateMode === 'custom' 
                  ? 'bg-[#8C6208] text-white shadow-xs' 
                  : 'bg-white text-[#5C4D42] hover:bg-[#F9F5EC] border border-[#E6D5B8]/60'
              }`}
            >
              Kustom
            </button>
          </div>

          {/* Date Picker / Day Navigator */}
          {dateMode === 'daily' && (
            <div className="flex items-center gap-2 w-full lg:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleShiftDay(-1)}
                className="h-8 w-8 p-0 rounded-lg border-[#E6D5B8] bg-white text-[#8C6208]"
                title="Hari Sebelumnya"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>

              <div className="relative flex items-center">
                <Input
                  type="date"
                  value={selectedDay}
                  onChange={e => { setSelectedDay(e.target.value); setCurrentPage(1); }}
                  className="h-8 text-xs font-bold bg-white border-[#E6D5B8] rounded-lg shadow-xs"
                />
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() => handleShiftDay(1)}
                className="h-8 w-8 p-0 rounded-lg border-[#E6D5B8] bg-white text-[#8C6208]"
                title="Hari Berikutnya"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>

              {selectedDay !== new Date().toISOString().split('T')[0] && (
                <Button
                  size="sm"
                  onClick={() => { setSelectedDay(new Date().toISOString().split('T')[0]); setCurrentPage(1); }}
                  className="h-8 text-xs font-bold bg-[#8C6208] text-white rounded-lg px-2.5"
                >
                  Hari Ini
                </Button>
              )}
            </div>
          )}

          {dateMode === 'custom' && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-[#7A695B]">Dari:</span>
              <Input
                type="date"
                value={customFrom}
                onChange={e => { setCustomFrom(e.target.value); setCurrentPage(1); }}
                className="h-8 text-xs bg-white border-[#E6D5B8] rounded-lg w-36"
              />
              <span className="text-xs text-[#7A695B]">s/d:</span>
              <Input
                type="date"
                value={customTo}
                onChange={e => { setCustomTo(e.target.value); setCurrentPage(1); }}
                className="h-8 text-xs bg-white border-[#E6D5B8] rounded-lg w-36"
              />
            </div>
          )}

          {dateMode === 'weekly' && (
            <span className="text-xs font-bold text-[#8C6208] bg-amber-50 px-3 py-1 rounded-lg border border-amber-200">
              7 Hari Terakhir
            </span>
          )}

          {dateMode === 'monthly' && (
            <span className="text-xs font-bold text-[#8C6208] bg-amber-50 px-3 py-1 rounded-lg border border-amber-200">
              Bulan Ini ({new Date().toLocaleString('id-ID', { month: 'long', year: 'numeric' })})
            </span>
          )}

          {dateMode === 'alltime' && (
            <span className="text-xs font-bold text-slate-700 bg-slate-100 px-3 py-1 rounded-lg">
              Seluruh Riwayat Sejak 1 September
            </span>
          )}

        </CardContent>
      </Card>

      {/* Level 1: Kategori Portal Utama */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-wider text-[#7A695B] flex items-center gap-1.5">
            <Layers className="w-3.5 h-3.5 text-[#8C6208]" />
            Pilih Kategori Portal:
          </span>
          <span className="text-xs text-slate-400 font-mono">
            {portalCounts[activePortal]} aktivitas
          </span>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
          {ALL_PORTALS.map(portal => {
            const Icon = portal.icon;
            const isActive = activePortal === portal.id;
            const count = portalCounts[portal.id] || 0;

            return (
              <button
                key={portal.id}
                onClick={() => {
                  setActivePortal(portal.id);
                  setActiveSubPage('all');
                  setCurrentPage(1);
                }}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap shadow-xs ${
                  isActive
                    ? `${portal.color} shadow-md scale-[1.02]`
                    : 'bg-white text-[#5C4D42] hover:bg-[#F9F5EC] border border-[#E6D5B8]/60'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{portal.name}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${
                  isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Level 2: Sub-Kategori Halaman Portal */}
      <div className="bg-[#FFFDF9] border border-[#E6D5B8]/60 p-3.5 rounded-2xl space-y-2 shadow-xs">
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#8C6208] flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5" />
            Sub-Kategori Halaman ({ALL_PORTALS.find(p => p.id === activePortal)?.name}):
          </span>
          {activeSubPage !== 'all' && (
            <button
              onClick={() => setActiveSubPage('all')}
              className="text-[11px] text-[#8C6208] font-bold hover:underline"
            >
              Lihat Semua Halaman
            </button>
          )}
        </div>

        {activePortal === 'all' ? (
          <div className="py-1 px-1 text-xs text-[#7A695B] flex items-center justify-between">
            <span>
              Sedang menampilkan aktivitas dari <b>Semua Portal</b>. Klik salah satu portal di atas jika ingin menyaring per menu/sub-halaman spesifik.
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 flex-wrap">
            {availableSubPages.map(page => {
              const isSubActive = activeSubPage === page.id;
              const subCount = subPageCounts[page.id] || 0;

              return (
                <button
                  key={page.id}
                  onClick={() => {
                    setActiveSubPage(page.id);
                    setCurrentPage(1);
                  }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    isSubActive
                      ? 'bg-[#8C6208] text-white shadow-xs'
                      : 'bg-white text-[#5C4D42] hover:bg-[#F9F5EC] border border-[#E6D5B8]/60'
                  }`}
                >
                  <span>{page.name}</span>
                  <span className={`px-1.5 py-0.2 rounded-full text-[9px] font-bold ${
                    isSubActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                  }`}>
                    {subCount}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Control Filters Bar */}
      <Card className="border border-[#E6D5B8]/60 bg-white shadow-xs">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            
            {/* Search Input */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Cari nama, invoice, kode Ref (misal: id_...)..."
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-9 text-xs h-9 bg-[#FFFDF9] border-[#E6D5B8]/80 rounded-xl"
              />
            </div>

            {/* Filter by Active Registered Staff */}
            <Select value={selectedStaff} onValueChange={v => { setSelectedStaff(v); setCurrentPage(1); }}>
              <SelectTrigger className="text-xs h-9 bg-[#FFFDF9] border-[#E6D5B8]/80 rounded-xl">
                <div className="flex items-center gap-2 truncate">
                  <User className="w-3.5 h-3.5 text-[#8C6208]" />
                  <SelectValue placeholder="Pilih User / Staff" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua User Satala ({activeStaffList.length} Akun)</SelectItem>
                {activeStaffList.map(mem => (
                  <SelectItem key={mem.name} value={mem.name}>
                    {mem.name} ({mem.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Filter by Action Type */}
            <Select value={selectedActionType} onValueChange={v => { setSelectedActionType(v); setCurrentPage(1); }}>
              <SelectTrigger className="text-xs h-9 bg-[#FFFDF9] border-[#E6D5B8]/80 rounded-xl">
                <div className="flex items-center gap-2 truncate">
                  <Filter className="w-3.5 h-3.5 text-[#8C6208]" />
                  <SelectValue placeholder="Tipe Aksi" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Jenis Aksi</SelectItem>
                <SelectItem value="update">Edit Data (Stok / Harga / Satuan / Nama)</SelectItem>
                <SelectItem value="create">Tambah Data Baru</SelectItem>
                <SelectItem value="delete">Hapus Data</SelectItem>
                <SelectItem value="masuk">Uang Masuk / Pembayaran</SelectItem>
                <SelectItem value="keluar">Uang Keluar / Biaya</SelectItem>
                <SelectItem value="approval">Persetujuan / Verifikasi</SelectItem>
              </SelectContent>
            </Select>

          </div>

          <div className="flex justify-between items-center text-xs text-[#7A695B] pt-1">
            <span>
              Menampilkan <b className="text-[#302621]">{filteredLogs.length}</b> aktivitas ditemukan ({dateMode === 'daily' ? formatDayTitle(selectedDay) : dateMode})
            </span>
            {(searchQuery || selectedStaff !== 'all' || selectedActionType !== 'all' || activeSubPage !== 'all' || dateMode !== 'daily' || selectedDay !== new Date().toISOString().split('T')[0]) && (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedStaff('all');
                  setSelectedActionType('all');
                  setDateMode('daily');
                  setSelectedDay(new Date().toISOString().split('T')[0]);
                  setCustomFrom('');
                  setCustomTo('');
                  setActiveSubPage('all');
                  setCurrentPage(1);
                }}
                className="text-xs font-bold text-[#8C6208] hover:underline"
              >
                Reset Semua Filter
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Activity Log Feed / Table */}
      <Card className="border border-[#E6D5B8]/60 bg-white shadow-xs overflow-hidden">
        <CardHeader className="p-4 border-b border-[#E6D5B8]/40 bg-[#FFFDF9]">
          <CardTitle className="text-sm font-black text-[#302621] flex items-center justify-between">
            <span>
              {dateMode === 'daily' ? `Riwayat Aktivitas: ${formatDayTitle(selectedDay)}` : 'Riwayat Aktivitas & Perubahan Data'}
            </span>
            <span className="text-xs font-normal text-[#7A695B]">
              Halaman {currentPage} dari {totalPages}
            </span>
          </CardTitle>
        </CardHeader>

        <CardContent className="p-0 divide-y divide-slate-100">
          {isLoading ? (
            <div className="p-12 text-center text-xs text-slate-500 space-y-2">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto text-[#8C6208]" />
              <p>Memuat seluruh log aktivitas sistem...</p>
            </div>
          ) : paginatedLogs.length === 0 ? (
            <div className="p-12 text-center text-xs text-slate-500 space-y-2">
              <History className="w-8 h-8 mx-auto text-slate-300" />
              <p className="font-bold text-slate-700">Tidak ada aktivitas pada tanggal / filter ini.</p>
              <p className="text-[11px] text-slate-400">
                {dateMode === 'daily' ? 'Coba ganti ke hari lain atau klik tombol "Semua Waktu".' : 'Coba ubah kata kunci atau pilih portal lain.'}
              </p>
            </div>
          ) : (
            paginatedLogs.map((log) => {
              const isMoneyIn = log.type === 'masuk' || log.action_type === 'payment';
              const isMoneyOut = log.type === 'keluar' || log.action_type === 'expense';
              const relTime = getRelativeTime(log.timestamp);
              const portalInfo = getPortalInfo(log.portal);
              const subInfo = getSubPageInfo(log.portal, log.page);

              return (
                <div
                  key={log.id}
                  className="p-4 hover:bg-[#FDFBF7] transition-colors flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 group"
                >
                  <div className="flex items-start gap-3 w-full sm:w-auto">
                    
                    {/* User Avatar Initials */}
                    <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-[#8C6208] to-[#C9A227] text-white flex items-center justify-center font-bold text-xs shrink-0 shadow-xs">
                      {log.user_name ? log.user_name.substring(0, 2).toUpperCase() : 'SY'}
                    </div>

                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-bold text-xs text-[#302621]">
                          {log.user_name}
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono">
                          • {log.user_role}
                        </span>

                        {/* Portal Badge */}
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold ${portalInfo.badgeColor}`}>
                          {portalInfo.name}
                        </span>

                        {/* Sub-Page Badge */}
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                          {subInfo.name}
                        </span>

                        {/* Action Type Badge */}
                        {log.action_type === 'update' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                            Edit Data
                          </span>
                        )}
                        {log.action_type === 'create' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                            Tambah Baru
                          </span>
                        )}
                        {log.action_type === 'delete' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
                            Hapus Data
                          </span>
                        )}
                        {log.action_type === 'approval' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                            Approval
                          </span>
                        )}

                        {relTime && (
                          <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200">
                            {relTime}
                          </span>
                        )}
                      </div>

                      <p className="text-xs font-semibold text-[#302621] leading-snug">
                        {log.title}
                      </p>

                      {log.details && (
                        <p className="text-[11px] text-[#7A695B] leading-relaxed">
                          {log.details}
                        </p>
                      )}

                      <div className="flex items-center gap-2 text-[10px] text-slate-400 font-mono pt-0.5 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-slate-400" />
                          {formatTimestamp(log.timestamp)}
                        </span>

                        {log.target_id && (
                          <button
                            onClick={() => handleRefClick(log.target_id)}
                            className="inline-flex items-center gap-1 bg-slate-100 hover:bg-amber-100 text-slate-700 hover:text-[#8C6208] px-2 py-0.5 rounded text-[10px] font-mono font-bold transition-colors cursor-pointer"
                            title="Klik untuk langsung memfilter riwayat Ref ini"
                          >
                            <span>Ref: {log.target_id}</span>
                            <Search className="w-2.5 h-2.5 opacity-60" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right Controls: Nominal + Action Buttons */}
                  <div className="flex items-center gap-2 self-end sm:self-center shrink-0">
                    
                    {log.amount !== null && log.amount > 0 && (
                      <div className="text-right mr-1">
                        <div className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-xl font-mono text-xs font-black shadow-xs ${
                          isMoneyIn 
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                            : 'bg-rose-50 text-rose-700 border border-rose-200'
                        }`}>
                          {isMoneyIn ? (
                            <ArrowDownLeft className="w-3.5 h-3.5 text-emerald-600" />
                          ) : (
                            <ArrowUpRight className="w-3.5 h-3.5 text-rose-600" />
                          )}
                          <span>{isMoneyIn ? '+' : '-'} Rp {Number(log.amount).toLocaleString('id-ID')}</span>
                        </div>
                        {log.account && (
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            {log.account}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Button 1: Lihat Detail Modal */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setInspectLog(log)}
                      className="h-8 px-2.5 text-xs font-bold border-[#E6D5B8] bg-white text-[#8C6208] hover:bg-[#F9F5EC] rounded-lg gap-1 shadow-xs"
                      title="Lihat Detail Lengkap Data Ini"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      <span className="hidden sm:inline">Detail</span>
                    </Button>

                    {/* Button 2: Buka Halaman Asli & Terisolasi */}
                    {subInfo.route && (
                      <Button
                        size="sm"
                        onClick={() => navigate(getDestinationUrl(log))}
                        className="h-8 px-2.5 text-xs font-bold bg-[#8C6208] hover:bg-[#724F06] text-white rounded-lg gap-1 shadow-xs"
                        title={`Buka Halaman ${subInfo.name} (Terisolasi)`}
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        <span className="hidden sm:inline">Buka</span>
                      </Button>
                    )}

                  </div>

                </div>
              );
            })
          )}
        </CardContent>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="p-3 border-t border-[#E6D5B8]/40 bg-[#FFFDF9] flex justify-between items-center text-xs">
            <span className="text-[#7A695B]">
              Halaman <b>{currentPage}</b> dari <b>{totalPages}</b>
            </span>

            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="h-8 px-3 text-xs border-[#E6D5B8]"
              >
                Sebelumnya
              </Button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum = i + 1;
                if (totalPages > 5 && currentPage > 3) {
                  pageNum = currentPage - 3 + i;
                  if (pageNum > totalPages) pageNum = totalPages - (4 - i);
                }
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className={`h-8 w-8 p-0 text-xs ${
                      currentPage === pageNum 
                        ? 'bg-[#8C6208] text-white' 
                        : 'border-[#E6D5B8] text-[#5C4D42]'
                    }`}
                  >
                    {pageNum}
                  </Button>
                );
              })}

              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="h-8 px-3 text-xs border-[#E6D5B8]"
              >
                Selanjutnya
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Modal Detail Inspeksi Audit */}
      <Dialog open={!!inspectLog} onOpenChange={(open) => !open && setInspectLog(null)}>
        <DialogContent className="sm:max-w-lg bg-white border border-[#E6D5B8] p-6 rounded-2xl shadow-xl">
          {inspectLog && (() => {
            const subInfo = getSubPageInfo(inspectLog.portal, inspectLog.page);
            const portalInfo = getPortalInfo(inspectLog.portal);

            return (
              <div className="space-y-4">
                <DialogHeader>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-[10px] font-bold ${portalInfo.badgeColor}`}>
                      {portalInfo.name}
                    </span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-[10px] font-semibold bg-slate-100 text-slate-700">
                      {subInfo.name}
                    </span>
                  </div>
                  <DialogTitle className="text-base font-black text-[#302621]">
                    {inspectLog.title}
                  </DialogTitle>
                  <DialogDescription className="text-xs text-[#7A695B]">
                    Inspeksi riwayat audit sistem dan pelacakan data resmi.
                  </DialogDescription>
                </DialogHeader>

                <div className="bg-[#FFFDF9] border border-[#E6D5B8]/80 p-4 rounded-xl space-y-3 text-xs">
                  
                  {/* User & Role */}
                  <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                    <span className="text-[#7A695B] font-medium">Pengguna (Staf Pelaksana):</span>
                    <div className="text-right">
                      <p className="font-bold text-[#302621]">{inspectLog.user_name}</p>
                      <p className="text-[10px] text-slate-400">{inspectLog.user_role}</p>
                    </div>
                  </div>

                  {/* Waktu */}
                  <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                    <span className="text-[#7A695B] font-medium">Waktu Transaksi (WIB):</span>
                    <span className="font-mono font-bold text-[#302621]">
                      {formatTimestamp(inspectLog.timestamp)}
                    </span>
                  </div>

                  {/* Kode Ref */}
                  {inspectLog.target_id && (
                    <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                      <span className="text-[#7A695B] font-medium">Kode Ref Dokumen:</span>
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono font-bold text-amber-800 bg-amber-50 px-2 py-0.5 rounded border border-amber-200 text-[11px]">
                          {inspectLog.target_id}
                        </span>
                        <button
                          onClick={() => handleCopyRef(inspectLog.target_id)}
                          className="p-1 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-800"
                          title="Salin Kode Ref"
                        >
                          {isCopiedRef ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Nominal */}
                  {inspectLog.amount !== null && inspectLog.amount > 0 && (
                    <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                      <span className="text-[#7A695B] font-medium">Nominal Transaksi:</span>
                      <span className="font-mono font-black text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded border border-emerald-200">
                        Rp {Number(inspectLog.amount).toLocaleString('id-ID')}
                      </span>
                    </div>
                  )}

                  {/* Rincian Keterangan */}
                  {inspectLog.details && (
                    <div className="space-y-1 pt-1">
                      <span className="text-[#7A695B] font-medium block">Rincian Informasi / Perubahan:</span>
                      <div className="bg-white p-2.5 rounded-lg border border-[#E6D5B8]/60 text-[#302621] text-[11px] leading-relaxed">
                        {inspectLog.details}
                      </div>
                    </div>
                  )}

                </div>

                <DialogFooter className="flex items-center justify-between sm:justify-between gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (inspectLog.target_id) {
                        handleRefClick(inspectLog.target_id);
                      }
                      setInspectLog(null);
                    }}
                    className="text-xs font-bold border-[#E6D5B8] gap-1"
                  >
                    <Search className="w-3.5 h-3.5" />
                    Filter Riwayat Dokumen Ini
                  </Button>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setInspectLog(null)}
                      className="text-xs"
                    >
                      Tutup
                    </Button>

                    {subInfo.route && (
                      <Button
                        size="sm"
                        onClick={() => {
                          const url = getDestinationUrl(inspectLog);
                          setInspectLog(null);
                          navigate(url);
                        }}
                        className="text-xs font-bold bg-[#8C6208] hover:bg-[#724F06] text-white gap-1 shadow-xs"
                      >
                        <span>Buka Halaman {subInfo.name}</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </DialogFooter>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

    </div>
  );
}
